/* ═══════════════════════════════════════════════════════════════════════════════
   EXISTING-BOOK RECOGNITION — shared recognition core for the DR engines.

   Pure function, no DB / no side-effects (mirrors the prepaidRouting.js Session-F
   extraction pattern). Collapses the two existing-book recognition shapes that were
   previously hand-copied across the deferred-revenue engines into ONE place,
   parameterized by renewal behavior:

     renewing === true  → FLAT  (renewing book: AE Quota, Company Quota)
       Two sub-modes, selected by `conserveTotal`:
       • conserveTotal SUPPLIED → FINITE conserving cohort. Recognizes the total over
         a FIXED 12-month recognition TERM from the first forecast month
         (addMonths(fm0, i), i=0..11) — the term is the annual recognition period,
         NEVER the forecast-window length — with the 12th CALENDAR month trued up to
         r2(conserveTotal − cumulative) so the book recognizes its exact total with a
         last-month remainder. The helper returns the full 12-month schedule; the
         caller clips to its own window (months past a short window simply aren't
         emitted). Window length therefore never affects the per-month amount — a
         9-month and a 12-month window produce identical month-by-month values for the
         months they share, and a future "extend forecast window" just reveals more of
         an already-computed schedule. Reproduces Company Quota's opening-ARR annual
         cohort byte-for-byte (closeRunRate = r2(openingARR/12), conserveTotal =
         openingARR).
       • conserveTotal OMITTED → PERPETUAL flat run-rate. A book that renews
         indefinitely ("assume all renew", e.g. AE Quota's existing book): every
         forecast month recognizes closeRunRate, no finite term, no remainder. The
         caller clips to its window. (Churn-of-book, if any, is applied by the caller
         — see computeAEQuotaDR.)

     renewing === false → DECAY (non-renewing book: Implementation)
       Each trailing per-actuals-month cohort recognizes amount/12 over a 12-month
       window measured from its OWN actuals month, then stops; the book runs off to
       zero if no new deals. Reproduces Implementation's Step 2 (and the legacy
       AE-Quota Step 3b) trailing-cohort run-off byte-for-byte, including the
       last-window-month remainder r2(amount − r2((amount/12)·11)).

   Churn and renewals are NOT handled here — callers apply those separately (they
   differ legitimately by engine). This helper emits ONLY the existing-book
   gaapRevenue contribution per forecast month.

   @param {Object}   args
   @param {boolean}  args.renewing       — true → FLAT, false → DECAY
   @param {string[]} args.forecastMonths — the month set to emit over (caller's own;
                                            CQ passes its 12 forecastMonths, Impl
                                            passes its 24-month internal horizon)
   @param {string}   args.closeMonth     — last actuals month "YYYY-MM"
   @param {number}  [args.closeRunRate]  — FLAT branch: monthly run-rate to hold
   @param {number}  [args.conserveTotal] — FLAT branch: exact total to conserve via
                                            last-month remainder (omit for pure flat)
   @param {Object}  [args.cohorts]       — DECAY branch: { [actualsMonth]: amount }
   @returns {Object} { [month]: number } — existing-book recognition per month. FLAT
            branch returns the full 12-month schedule keyed by calendar month; DECAY
            branch returns per-forecast-month run-off. Caller clips to its window.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { addMonths } from './dateUtils';

const RECOGNITION_TERM = 12; // annual recognition period — fixed, NOT window length

const r2 = n => Math.round(n * 100) / 100;

export function recognizeExistingBook({ renewing, forecastMonths, closeMonth, closeRunRate, conserveTotal, cohorts, termMonths, exact }) {
  const months = forecastMonths || [];
  const out = {};
  months.forEach(m => { out[m] = 0; });
  if (!months.length) return out;

  if (renewing) {
    const rate = r2(closeRunRate || 0);

    if (conserveTotal == null) {
      // ── FLAT (perpetual) ── a renewing book that renews indefinitely ("assume all
      // renew", e.g. AE Quota's existing book): hold the run-rate for every forecast
      // month, no finite term, no remainder. The caller clips emission to its window.
      months.forEach(m => {
        if (closeMonth == null || m > closeMonth) out[m] = rate;
      });
      return out;
    }

    // ── FLAT (finite conserving cohort) ── recognize conserveTotal over a FIXED
    // 12-month term from the first forecast month, remainder trued up on calendar-
    // month-12 to conserve the exact total (e.g. Company Quota's opening-ARR cohort).
    // Window length never affects the per-month amount — the caller clips emission.
    const fm0 = months[0];
    let cum = 0;
    for (let i = 0; i < RECOGNITION_TERM; i++) {
      const recMonth = addMonths(fm0, i);
      const isLast = i === RECOGNITION_TERM - 1;
      const recAmt = isLast ? r2(conserveTotal - cum) : rate;
      cum = r2(cum + recAmt);
      out[recMonth] = recAmt;
    }
    return out;
  }

  // ── DECAY branch (non-renewing trailing-cohort run-off) ──
  /* ★★ PART B — THE TERM IS NOW A PARAMETER, DEFAULTING TO 12.
   * ⛔ THIS IS A SHARED FILE, and that is why the default exists: `termMonths` is OPTIONAL and
   *   falls back to RECOGNITION_TERM (12), so ANY caller that does not pass it is byte-identical
   *   to before. Measured: this branch has exactly ONE caller (computeImplementationRevenue) —
   *   AE and CQ take the `renewing === true` FLAT branch above and never reach this code — so
   *   the change cannot reach them BY CONSTRUCTION.
   * ⛔ BUT THE ACCEPTANCE ASSERTS AE/CQ BYTE-IDENTICAL RATHER THAN REASONING THAT IT CANNOT.
   *   "It has one caller" is exactly the shape of claim that has been measured wrong in this
   *   project before (§12's CATS/ALL_D keep-note: a confident doc note is an input to be
   *   measured, not a fact to be inherited).
   * ★ WHY THE RUN-OFF MUST TAKE THE SAME TERM AS THE FORWARD COHORTS: they are the SAME LINE's
   *   engagements. Leaving this at 12 while Step 1 uses `length_months` would have one
   *   implementation line recognising its NEW bookings over 6 months and its EXISTING book over
   *   12 — one rule, two answers, inside a single line. That is the divergence §6 forbids. */
  const term = Number.isInteger(termMonths) && termMonths > 0 ? termMonths : RECOGNITION_TERM;
  const src = cohorts || {};
  const actualsMonths = Object.keys(src).filter(m => m <= closeMonth).sort();
  months.forEach(fm => {
    if (fm <= closeMonth) return;
    const [fy, fmo] = fm.split('-').map(Number);
    actualsMonths.forEach(am => {
      const amount = r2(parseFloat(src[am]) || 0);
      if (amount === 0) return;
      const [ay, amo] = am.split('-').map(Number);
      const monthsElapsed = (fy - ay) * 12 + (fmo - amo) + 1; // 1-based (calendar arithmetic — the 12 here is MONTHS-PER-YEAR, not the term)
      if (monthsElapsed > term || monthsElapsed < 1) return;   // outside the recognition window
      /* W-081 (2026-10-05): `exact` -- UNROUNDED shares (amount/term every month, no remainder); the caller
         (computeImplementationRevenue) rounds ONCE, on the line's cumulative, so the tail adds up to what the GL
         closed at. Omitted -> the rounded per-cohort shape below, unchanged. */
      if (exact) { out[fm] += amount / term; return; }
      const monthlyRec = r2(amount / term);
      // Last window month gets the remainder to prevent float drift.
      const recAmt = monthsElapsed < term ? monthlyRec : r2(amount - r2(monthlyRec * (term - 1)));
      out[fm] = r2(out[fm] + recAmt);
    });
  });
  return out;
}

export { r2 };
